import React from 'react';
import { Redirect, withRouter } from 'react-router';
import { connect } from 'react-redux';
import i18n from 'util/i18n';
import {
  fetchCheckStatusRequest,
  FetchCheckStatusRequestAction,
  FetchCheckStatusResponseData,
  submitIdentityVerificationRequest,
  SubmitIdentityVerificationRequestAction,
  SubmitIdentityVerificationResponseData
} from '@redux/modules/api/actions';
import { RootState } from 'types/state';
import ChallengeQuestions from 'components/Form/ChallengeQuestions/ChallengeQuestions';
import { AppKeydownWatchers } from 'util/singletons';
import Introduction from 'components/Form/Introduction';
import StickyHeader from 'components/Sticky/StickyHeader';
import OfficialWebsite from 'components/OfficialWebsite';
import HeaderBanner from 'components/HeaderBanner';
import { ProgressBar } from 'components/ProgressBar';
import TimeoutWarning from 'components/Form/TimeoutWarning';
import { renewSessionRequest, RenewSessionRequestAction } from '@redux/modules/authentication/actions';
import { AuthenticationState } from '@redux/modules/authentication/reducer';
import { APIState } from '@redux/modules/api/reducer';
import applicationReducer, { ApplicationSettingsState } from '@redux/modules/application/reducer';

type VerificationPageDispatchProps = {
  dispatchFetchCheckStatusRequest: () => void;
  dispatchSubmitIdentityVerificationRequest: (ssn: string, dob: string) => void;
  keepSessionAlive: () => void;
}

type VerificationPageStateProps = {
  apiVersionText: string;
  clientVersionText: string;
  verifyPassword: boolean
  successfulCheckStatusFetch: boolean
  mobileNavigation: boolean;
  showSessionWarning: boolean;
  identityVerificationResponseReceived: boolean;
  successfulIdentityVerification: boolean;

}
type State = {
  verificationAttempts: number;
}
type VerificationPageProps = VerificationPageDispatchProps & VerificationPageStateProps;

export class VerificationPage extends React.Component<VerificationPageProps, State> {
  /** Tick every minute */
  private static readonly REFRESH_INTERVAL = 60_000;

  private refreshTimerId = 0;

  constructor(props: VerificationPageProps) {
    super(props);

    this.state = {
      verificationAttempts: 0,
    };
  }

  componentDidMount(): void {
    this.props.dispatchFetchCheckStatusRequest();

    const appKeydownWatchers: AppKeydownWatchers = AppKeydownWatchers.getInstance();

    appKeydownWatchers.watchErrorDebugger(true);

    appKeydownWatchers.watchUserActivity(true);

    this.startRefreshTimer();
  }

  componentWillUnmount(): void {
    const appKeydownWatchers: AppKeydownWatchers = AppKeydownWatchers.getInstance();

    appKeydownWatchers.watchErrorDebugger(false);

    appKeydownWatchers.watchUserActivity(false);

    window.clearInterval(this.refreshTimerId);
  }

  /**
  This timer will run every minute for the duration of the user using the application.
  In addition refreshing will occur when the user saves.
  In any given minute where user activity occurs (typing or clicking) the session will be refreshed.
  This refresh action will occur within one minute of the user's manual action
  If the session timeout is nearly up the modal will pop up to have the user manually push the button.
  These session refreshes are separate from save events which occur regularly and do not require a
  session refresh upon each action.

  This particular model will not allow the API to have more than SessionTimeout between client interactions;
  however, some client interaction may be deferred by up to a minute. This spares client and server processing by
  using each minute to batch refreshes resulting in clients having no more than one refresh per minute. This
  monitors mouse click interaction and keyboard button presses.

  The purpose of this is a person who is activly typing or clicking in any section, reviewing, inputting or
  otherwise interacting should not be interrupted with timeout notices. A user who remains idle and waits until
  the final minute before the timeout is displayed to interact with the screen may still be presented with the continue
  session timeout modal, but should have all data auto saved.
  If the user's last action was within the final minute of the timeout.
  This will fully prevent an active user who is using the system encountering an unexpected timeout.
  */
  private startRefreshTimer(): void {
    this.refreshTimerId = window.setInterval(this.props.keepSessionAlive, VerificationPage.REFRESH_INTERVAL);
  }

  submitChallengeRequest = (ssn: string, dob: string) => {
    if (this.state.verificationAttempts < 8) {
      this.props.dispatchSubmitIdentityVerificationRequest(ssn, dob);
      const verifications = (this.state.verificationAttempts || 0) + 1; // eslint-disable-line react/no-access-state-in-setstate
      this.setState({ verificationAttempts: verifications });
    }
  };

  render() {
    const {
      apiVersionText,
      clientVersionText,
      mobileNavigation,
      showSessionWarning,
    } = this.props;

    if (!this.props.successfulCheckStatusFetch) {
      return null;
    }

    if (!this.props.verifyPassword || this.props.successfulIdentityVerification) {
      return (
        <Redirect to="/landingpage" />
      );
    }

    const verificationAttempts = this.state.verificationAttempts;
    let preventFurtherValidation = false;
    if (this.state.verificationAttempts > 7) {
      preventFurtherValidation = true;
    }

    return (
      <div className="verification-page">
        <Introduction />
        <div id="scrollTo" />
        <a className="usa-skipnav" href="#main-content">
          {i18n.t('app.skip')}
        </a>
        <StickyHeader stickyClass="sticky-header">
          <div className="header-container">
            <div className="header">
              <header className="usa-header usa-header-basic" role="banner">
                <OfficialWebsite />
                <HeaderBanner
                  mobileNavigation={mobileNavigation}
                  showSurvey={false}
                  submitted={false}
                />
              </header>
              <div id="scrollToProgress" />
              <div className="usa-overlay" />
              <ProgressBar />
            </div>
          </div>
        </StickyHeader>
        <div className="content">
          <h4 style={{ paddingLeft: '30px', paddingRight: '30px' }}>You have successfully logged into you account. <br />
            The eApp application requires this account be validated before accessing the personal information associated to it upon
            initial access and when certain account changes occur.<br />
            Once validated you will be provided the details on your form(s).
          </h4>
        </div>
        {verificationAttempts > 0 && (
        <h5 style={{ paddingLeft: '30px', paddingRight: '30px', textAlign: 'center' }}>Verification Attempt {verificationAttempts}</h5>
        )}

        { !preventFurtherValidation && (
        <ChallengeQuestions
          identityVerificationResponseReceived={this.props.identityVerificationResponseReceived}
          submitChallengeResponse={this.submitChallengeRequest}
          successfulIdentityVerification={this.props.successfulIdentityVerification}
        />
        )}
        {preventFurtherValidation && (
          <h4 style={{ paddingLeft: '30px', paddingRight: '30px', textAlign: 'center' }}>You cannot be verified at this time please try again later. {`${(new Date()).toISOString()}`}</h4>
        )}
        <div
          style={{
            position: 'absolute', top: '0px', right: '0px', width: '20px', height: '40px'
          }}
          title={`Client Version: ${clientVersionText}\nAPI Version: ${apiVersionText}\n`}
        >
          &nbsp;
        </div>
        {showSessionWarning && (<TimeoutWarning />)}
      </div>
    );
  }
}

function mapStateToProps(state: RootState): VerificationPageStateProps {
  const { isLoading: isCheckStatusLoading, hasError: hasCheckStatusError, response: checkStatusResponse } = state.api.fetchCheckStatus;

  const successfulCheckStatusFetch = !isCheckStatusLoading && !hasCheckStatusError && Boolean(checkStatusResponse);

  const identityVerificationResponse = state.api.submitIdentityVerification.response;

  const identityVerificationSuccessValue: boolean | undefined = (identityVerificationResponse as SubmitIdentityVerificationResponseData)?.success;

  const identityVerificationResponseReceived = identityVerificationSuccessValue === true || identityVerificationSuccessValue === false;

  const successfulIdentityVerification = identityVerificationSuccessValue === true;

  const api: APIState = state.api;

  const apiVersionText: string = api.apiVersion.apiVersionText || '';

  const clientVersionText: string = api.clientVersion.clientVersionText || '';

  const application: ReturnType<typeof applicationReducer> = state.application;

  const applicationSettings: ApplicationSettingsState = application.Settings;

  const mobileNavigation = Boolean(applicationSettings.mobileNavigation);

  const authentication: AuthenticationState = state.authentication;

  const showSessionWarning = authentication.showSessionWarning;

  return {
    verifyPassword: Boolean((checkStatusResponse as FetchCheckStatusResponseData)?.verifyPassword),
    successfulCheckStatusFetch,
    apiVersionText,
    clientVersionText,
    mobileNavigation,
    showSessionWarning,
    identityVerificationResponseReceived,
    successfulIdentityVerification
  };
}

function mapDispatchToProps(dispatch: (action:
  | FetchCheckStatusRequestAction
  | SubmitIdentityVerificationRequestAction
  | RenewSessionRequestAction) => void): VerificationPageDispatchProps {
  return {
    dispatchFetchCheckStatusRequest: () => {
      dispatch(fetchCheckStatusRequest());
    },

    dispatchSubmitIdentityVerificationRequest: (SSN: string, DOB: string) => {
      dispatch(submitIdentityVerificationRequest({ SSN, DOB }));
    },
    keepSessionAlive: () => {
      const watcher: AppKeydownWatchers = AppKeydownWatchers.getInstance();

      if (watcher.data.userActivity) {
        dispatch(renewSessionRequest(null));

        watcher.restartUserActivity();
      }
    },
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

export default withRouter(connector(VerificationPage));
